import React, { useState, useRef, useEffect } from 'react';
import { Printer, Download, ArrowLeft, Building2, Calendar, User, Hash, MapPin } from 'lucide-react';

const PurchasePrintForm = () => {
  const printRef = useRef();
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchaseItemData, setPurchaseItemData] = useState(null);
  const [error, setError] = useState(null);

  // Get ID from query string - for demo, we'll use a mock ID
  const queryParams = new URLSearchParams(window.location.search);
  const id = queryParams.get("id") || "demo-123";

  // Default/Mock purchase data
  const defaultData = {
    company: {
      name: "NISSI INFOTECH",
      tagline: "CIVIL CONSTRUCTION",
      address: "3/42-12, Nagappa Colony, Kavundampalayam, Salem-636030, Tamil Nadu, India",
      phone: "Phone No. 9003744644",
      email: "infraconsoft.implementation@nissiinfotech.in"
    },
    requisition: {
      purchaseReqNo: "PR-2024-001",
      branchName: "HEAD-OFFICE",
      reqFor: "own",
      purchaseReqDate: "05/05/2022",
      department: "HILARA"
    },
    items: [
      {
        sno: 1,
        itemCode: "457278397",
        description: "Tiles",
        specification: "",
        life: "5 Years",
        previousDate: "01/04/2022",
        uom: "SF",
        reqQty: "50.00",
        previousPrice: "₹40.00",
        previousAmount: "₹2,000.00",
        dueDate: "07/05/2022",
        purpose: "Construction Work"
      },
      {
        sno: 2,
        itemCode: "031112002",
        description: "Mulching machine",
        specification: "Heavy Duty",
        life: "10 Years",
        previousDate: "01/04/2022",
        uom: "Nos",
        reqQty: "2.00",
        previousPrice: "₹25,000.00",
        previousAmount: "₹50,000.00",
        dueDate: "10/05/2022",
        purpose: "Site Work"
      }
    ],
    totalAmount: "₹52,000.00",
    workflow: {
      preparedBy: "SLTNEW002",
      preparedDate: "05/05/2022",
      preparedTime: "4:37AM",
      checkedBy: "MANAGER01",
      checkedDate: "05/05/2022",
      checkedTime: "2:15PM",
      approvedBy: "SLTNEW002",
      approvedDate: "05/05/2022",
      approvedTime: "4:38AM"
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    console.log("Download functionality would be implemented here");
    // You can implement PDF generation here
  };

  const handleBack = () => {
    console.log("Navigate back");
    // window.history.back() or navigate using router
  };

  useEffect(() => {
     const fetchData = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `https://www.nissi-india.com/api/purchase_reqPrint1/${id}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const apiData = await response.json();
        console.log("API Response:", apiData);

        if (apiData.success && apiData.data && apiData.data.length > 0) {
          const apiItems = apiData.data.map((item, idx) => ({
            sno: idx + 1,
            itemCode: item.fld_itemcode,
            description: item.fld_itemname,
            specification: item.fld_specification || "",
            life: "-", // not present in API
            previousDate: item.previousdate || "-",
            uom: item.fld_uomcode,
            reqQty: item.fld_qty,
            previousPrice: item.Price ,
            previousAmount: item.amount ? `${item.cursym}${item.amount}` : "0.00",
            dueDate: item.fld_duedate,
            purpose: item.purpose || "-"
          }));

          const transformedData = {
            company: {
              name: "NISSI INFOTECH",
              tagline: "CIVIL CONSTRUCTION",
              address:
                "3/42-12, Nagappa Colony, Kavundampalayam, Salem-636030, Tamil Nadu, India",
              phone: "Phone No. 9003744644",
              email: "infraconsoft.implementation@nissiinfotech.in"
            },
            requisition: {
              purchaseReqNo: apiData.data[0].fld_purchaserequestno,
              branchName: apiData.data[0].fld_branchname,
              reqFor: apiData.data[0].fld_Indentref,
              purchaseReqDate: apiData.data[0].PRdate,
              department: apiData.data[0].fld_departmentname
            },
            items: apiItems,
            totalAmount: `${apiData.data[0].cursym}${apiItems.reduce(
              (sum, itm) =>
                sum +
                (parseFloat(itm.reqQty) *
                  (parseFloat(apiData.data.find(x => x.fld_itemcode === itm.itemCode)?.fld_itemPrice) || 0)),
              0
            )}`,
            workflow: {
              preparedBy: apiData.data[0].preparedby,
              preparedDate: apiData.data[0].PRdate,
              preparedTime: apiData.data[0].PRTime,
              checkedBy: apiData.data[0].checkedBy || "Pending",
              checkedDate: apiData.data[0].checkedDate || "",
              checkedTime: apiData.data[0].checkedTime || "",
              approvedBy: apiData.data[0].Approvedby,
              approvedDate: apiData.data[0].Approveddate,
              approvedTime: apiData.data[0].ApprovedTime
            }
          };

          setPurchaseItemData(transformedData);
          setError(null);
        } else {
          throw new Error("No data found");
        }
      } catch (err) {
        console.error("Error fetching purchase data:", err);
        setError(err.message);
        setPurchaseItemData(defaultData); // fallback
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading purchase requisition...</p>
        </div>
      </div>
    );
  }

  if (!purchaseItemData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="text-lg font-semibold mb-2">Error Loading Data</p>
          <p>{error || "Failed to load purchase requisition data"}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            font-family: 'Times New Roman', Times, serif;
            font-size: 12pt;
            line-height: 1.4;
          }
          
          .print-container {
            width: 100%;
            max-width: none;
            margin: 0;
            padding: 0;
            box-shadow: none;
            border: none;
          }
          
          .print-header {
            border-bottom: 2px solid #000;
            margin-bottom: 20pt;
          }
          
          .logo-container {
            width: 80pt;
            height: 80pt;
            border: 2px solid #000;
            display: inline-block;
            vertical-align: top;
            margin-right: 20pt;
          }
          
          .logo-container img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
          
          .print-title {
            font-size: 16pt;
            font-weight: bold;
            text-align: center;
            margin: 15pt 0;
            text-transform: uppercase;
            letter-spacing: 1pt;
          }
          
          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin: 15pt 0;
          }
          
          .print-table th,
          .print-table td {
            border: 1px solid #000;
            padding: 6pt;
            text-align: left;
            vertical-align: top;
          }
          
          .print-table th {
            background-color: #f0f0f0;
            font-weight: bold;
            text-align: center;
          }
          
          .text-right {
            text-align: right;
          }
          
          .signature-section {
            margin-top: 40pt;
            page-break-inside: avoid;
          }
          
          .signature-box {
            display: inline-block;
            width: 30%;
            margin-right: 3%;
            vertical-align: top;
            border: 1px solid #000;
            padding: 10pt;
            min-height: 60pt;
          }
          
          .total-section {
            margin: 20pt 0;
            text-align: right;
          }
          
          .total-amount {
            font-size: 14pt;
            font-weight: bold;
            border: 2px solid #000;
            padding: 10pt;
            display: inline-block;
            margin-top: 10pt;
          }

          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-gray-50">
        {/* API Error Warning */}
        {error && (
          <div className="no-print mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded mx-4 mt-4">
            <p className="text-sm">
              <strong>Note:</strong> Using sample data due to API error. ({error})
            </p>
          </div>
        )}

        {/* Header Controls - Hidden in print */}
        <div className="print:hidden no-print bg-white shadow-sm border-b sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ArrowLeft size={18} />
                Back
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsPreviewMode(!isPreviewMode)}
                  className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors text-sm"
                >
                  {isPreviewMode ? 'Edit Mode' : 'Preview Mode'}
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white hover:bg-green-700 rounded-md transition-colors text-sm"
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors text-sm"
                >
                  <Printer size={16} />
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Print Content */}
        <div className="max-w-4xl mx-auto p-4 print:p-0 print:max-w-none">
          <div
            ref={printRef}
            className="print-container bg-white shadow-lg print:shadow-none border print:border-0 min-h-[297mm]"
            style={{ fontFamily: 'Times New Roman, Times, serif' }}
          >
            {/* Company Header */}
            <div className="print-header px-6 py-4 print:px-0 print:py-0 border-b-2 border-black print:border-b-2">
              <div className="flex items-start justify-between print:justify-center">
                {/* Logo Section */}
                <div className="flex-shrink-0 mr-6 print:mr-8">
                  <div className="w-20 h-20 print:w-24 print:h-24 border-2 border-gray-400 rounded-lg flex items-center justify-center bg-gray-50 print:bg-white print:border-black print:rounded-none">
                    {/* Fallback text logo */}
                    <div className="w-full h-full flex items-center justify-center text-center">
                      <div>
                        <div className="text-xs font-bold text-blue-800">NISSI</div>
                        <div className="text-xs font-bold text-blue-600">INFOTECH</div>
                        <div className="text-xs text-gray-600">PVT LTD</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Information */}
                <div className="flex-1 text-center print:text-center">
                  <h1 className="text-2xl print:text-3xl font-bold mb-1 tracking-wide">
                    {purchaseItemData.company.name}
                  </h1>
                  <p className="text-lg print:text-xl font-semibold text-gray-700 mb-2 tracking-wide">
                    {purchaseItemData.company.tagline}
                  </p>
                  <div className="text-sm print:text-base space-y-1">
                    <p className="font-medium">{purchaseItemData.company.address}</p>
                    <div className="flex justify-center gap-8 text-sm">
                      <span>{purchaseItemData.company.phone}</span>
                      <span>{purchaseItemData.company.email}</span>
                    </div>
                  </div>
                </div>

                {/* Right side spacer for balance (hidden on print) */}
                <div className="w-20 print:hidden"></div>
              </div>
            </div>

            {/* Document Title */}
            <div className="print-title py-4 print:py-3">
              <h2 className="text-xl print:text-2xl font-bold text-center tracking-wide">
                PURCHASE REQUISITION REPORT
              </h2>
            </div>

            {/* Content */}
            <div className="px-6 print:px-0">
              {/* Requisition Details */}
              <div className="mb-6 print:mb-4">
                <table className="w-full text-sm print:text-base">
                  <tbody>
                    <tr>
                      <td className="py-2 font-semibold w-1/4">Purchase Req. No:</td>
                      <td className="py-2 border-b border-dotted border-gray-400">
                        {purchaseItemData.requisition.purchaseReqNo}
                      </td>
                      <td className="py-2 font-semibold w-1/4 pl-8">Branch Name:</td>
                      <td className="py-2 border-b border-dotted border-gray-400">
                        {purchaseItemData.requisition.branchName}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 font-semibold">Purchase Req. Date:</td>
                      <td className="py-2 border-b border-dotted border-gray-400">
                        {purchaseItemData.requisition.purchaseReqDate}
                      </td>
                      <td className="py-2 font-semibold pl-8">Department:</td>
                      <td className="py-2 border-b border-dotted border-gray-400">
                        {purchaseItemData.requisition.department}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 font-semibold">Req For:</td>
                      <td className="py-2 border-b border-dotted border-gray-400 capitalize">
                        {purchaseItemData.requisition.reqFor}
                      </td>
                      <td className="pl-8"></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Items Table */}
              <div className="mb-6 print:mb-4">
                <h3 className="text-lg print:text-xl font-bold mb-3 print:mb-2">ITEM DETAILS</h3>
                <div className="overflow-x-auto">
                  <table className="print-table w-full border-collapse border border-black text-xs print:text-sm">
                    <thead>
                      <tr className="bg-gray-100 print:bg-gray-200">
                        <th className="border border-black p-2 text-center font-bold">S.No</th>
                        <th className="border border-black p-2 text-center font-bold">Item Code</th>
                        <th className="border border-black p-2 text-center font-bold">Description</th>
                        <th className="border border-black p-2 text-center font-bold">Life</th>
                        <th className="border border-black p-2 text-center font-bold">Previous Date</th>
                        <th className="border border-black p-2 text-center font-bold">UOM</th>
                        <th className="border border-black p-2 text-center font-bold">Req Qty</th>
                        <th className="border border-black p-2 text-center font-bold">Previous Price</th>
                        <th className="border border-black p-2 text-center font-bold">Previous Amount</th>
                        <th className="border border-black p-2 text-center font-bold">Due Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseItemData.items.map((item, index) => (
                        <tr key={index}>
                          <td className="border border-black p-2 text-center">{item.sno}</td>
                          <td className="border border-black p-2 text-center font-medium">{item.itemCode}</td>
                          <td className="border border-black p-2">{item.description}</td>
                          <td className="border border-black p-2 text-center">{item.life || '-'}</td>
                          <td className="border border-black p-2 text-center">{item.previousDate}</td>
                          <td className="border border-black p-2 text-center">{item.uom}</td>
                          <td className="border border-black p-2 text-right font-medium">{item.reqQty}</td>
                          <td className="border border-black p-2 text-right font-medium">{item.previousPrice}</td>
                          <td className="border border-black p-2 text-right font-bold">{item.previousAmount}</td>
                          <td className="border border-black p-2 text-center">{item.dueDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Total Amount */}
                <div className="total-section mt-4 print:mt-3 text-right">
                  <div className="total-amount inline-block border-2 border-black p-3 print:p-2">
                    <span className="text-base print:text-lg font-bold">
                      TOTAL AMOUNT: {purchaseItemData.totalAmount}
                    </span>
                  </div>
                </div>
              </div>

              {/* Workflow Signatures */}
              <div className="signature-section mt-8 print:mt-6 print:page-break-inside-avoid">
                <h3 className="text-lg print:text-xl font-bold mb-4 print:mb-3">APPROVAL WORKFLOW</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:gap-0">
                  <div className="signature-box border border-black p-3 print:inline-block print:w-30 print:mr-3">
                    <div className="mb-2">
                      <h4 className="font-bold text-sm print:text-base">PREPARED BY</h4>
                    </div>
                    <p className="font-medium mb-1">{purchaseItemData.workflow.preparedBy}</p>
                    <p className="text-sm">{purchaseItemData.workflow.preparedDate}</p>
                    <p className="text-sm">{purchaseItemData.workflow.preparedTime}</p>
                    <div className="mt-4 border-t border-black pt-2">
                      <p className="text-xs text-gray-600">Signature & Date</p>
                    </div>
                  </div>

                  <div className="signature-box border border-black p-3 print:inline-block print:w-30 print:mr-3">
                    <div className="mb-2">
                      <h4 className="font-bold text-sm print:text-base">CHECKED BY</h4>
                    </div>
                    <p className="font-medium mb-1">{purchaseItemData.workflow.checkedBy || 'Pending'}</p>
                    {purchaseItemData.workflow.checkedDate && (
                      <>
                        <p className="text-sm">{purchaseItemData.workflow.checkedDate}</p>
                        <p className="text-sm">{purchaseItemData.workflow.checkedTime}</p>
                      </>
                    )}
                    <div className="h-12 print:h-16 mt-4"></div>
                    <div className="mt-4 border-t border-black pt-2">
                      <p className="text-xs text-gray-600">Signature & Date</p>
                    </div>
                  </div>

                  <div className="signature-box border border-black p-3 print:inline-block print:w-30">
                    <div className="mb-2">
                      <h4 className="font-bold text-sm print:text-base">APPROVED BY</h4>
                    </div>
                    <p className="font-medium mb-1">{purchaseItemData.workflow.approvedBy}</p>
                    <p className="text-sm">{purchaseItemData.workflow.approvedDate}</p>
                    <p className="text-sm">{purchaseItemData.workflow.approvedTime}</p>
                    <div className="mt-4 border-t border-black pt-2">
                      <p className="text-xs text-gray-600">Signature & Date</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 print:mt-6 pt-4 border-t border-gray-300 text-center">
                <p className="text-xs print:text-sm text-gray-600 mb-1">
                  Report Generated by Infraconsoft - www.infraconsoft.com
                </p>
                <p className="text-xs text-gray-500">
                  Generated on: {new Date().toLocaleDateString('en-IN')} | Page 1 of 1
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchasePrintForm;